import type { Permission } from "src/api/client-api/permissions/useGetAllPermissions";
import { CREATE, DELETE, MODIFY, VIEW } from "src/context/FoodpilotContext";

export const CORE_ACTION_ORDER = [VIEW, CREATE, MODIFY, DELETE] as const;

export type ResourceGroup = {
    object: string;
    permissions: Permission[];
    core: Record<string, Permission | undefined>;
    specific: Permission[];
};

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

export const groupPermissionsByObject = (permissions: Permission[]): ResourceGroup[] => {
    const grouped = new Map<string, Permission[]>();

    permissions.forEach((permission) => {
        const bucket = grouped.get(permission.object) ?? [];
        bucket.push(permission);
        grouped.set(permission.object, bucket);
    });

    return [...grouped.entries()]
        .sort(([objectA], [objectB]) => collator.compare(objectA, objectB))
        .map(([object, bucket]) => {
            const core: Record<string, Permission | undefined> = {};
            const specific: Permission[] = [];

            bucket.forEach((permission) => {
                if ((CORE_ACTION_ORDER as readonly string[]).includes(permission.slug)) {
                    core[permission.slug] = permission;
                } else {
                    specific.push(permission);
                }
            });

            specific.sort((a, b) => collator.compare(a.slug, b.slug));

            return { object, permissions: bucket, core, specific };
        });
};

export const resolveCoreColumns = (groups: ResourceGroup[]): string[] => {
    const present = new Set<string>();

    groups.forEach((group) => {
        CORE_ACTION_ORDER.forEach((slug) => {
            if (group.core[slug] !== undefined) {
                present.add(slug);
            }
        });
    });

    return CORE_ACTION_ORDER.filter((slug) => present.has(slug));
};

export const matchesSearch = (permission: Permission, query: string): boolean => {
    if (query === "") {
        return true;
    }

    const needle = query.toLowerCase();

    return (
        permission.object.toLowerCase().includes(needle) ||
        permission.slug.toLowerCase().includes(needle) ||
        permission.identifier.toLowerCase().includes(needle)
    );
};
